'use client';

import type { ReactElement } from 'react';
import { Fragment, useState } from 'react';
import { Controller, type Control } from 'react-hook-form';
import { FormField } from '@/components/inventory/inventory-form-card/shared/FormField';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { tokens } from '@/lib/design-tokens';
import { useOptionalFeatureFlags } from '@/lib/providers/feature-flags-provider';
import type { Contact } from '@/lib/services/contacts/types';
import type { ContactFormSectionProps } from './types';

export function NotesSection({
  control,
  mode,
  values,
  clickToCopy = false,
  showMatterNotes = false
}: ContactFormSectionProps): ReactElement {
  const { hasFeature } = useOptionalFeatureFlags();
  const contactsFeatureEnabled = hasFeature('contacts-page');
  const [showNotes, setShowNotes] = useState<boolean>(!!values?.notes);
  const [showMatterNotesField, setShowMatterNotesField] = useState<boolean>(
    !!values?.matterNotes
  );

  if (mode === 'read') {
    const hasNotes = contactsFeatureEnabled && !!values?.notes;
    const hasMatterNotes = showMatterNotes && !!values?.matterNotes;

    if (!hasNotes && !hasMatterNotes) {
      return <Fragment />;
    }

    return (
      <Fragment>
        {hasMatterNotes && (
          <FormField
            label="Notes"
            value={values!.matterNotes!}
            mode="read"
            clickToCopy={clickToCopy}
          />
        )}
        {hasNotes && (
          <FormField
            label="General Notes"
            value={values!.notes!}
            mode="read"
            clickToCopy={clickToCopy}
          />
        )}
      </Fragment>
    );
  }

  return (
    <Fragment>
      {showMatterNotes && (
        <>
          {!showMatterNotesField ? (
            <Button
              size="xs"
              density="compact"
              width="full"
              align="start"
              type="button"
              variant="ghost"
              onClick={() => setShowMatterNotesField(true)}
            >
              + Add Notes
            </Button>
          ) : (
            <Controller
              control={control as Control<Partial<Contact>>}
              name="matterNotes"
              render={({ field, fieldState }) => (
                <div className={tokens.patterns.form.spacing}>
                  <label
                    htmlFor="contactMatterNotes"
                    className={tokens.patterns.form.label}
                  >
                    Notes
                  </label>
                  <Textarea
                    id="contactMatterNotes"
                    placeholder="Add notes specific to this contact's role in this matter..."
                    value={(field.value as string) || ''}
                    onChange={(e) => field.onChange(e.target.value)}
                    rows={4}
                    className={cn(
                      'min-h-[80px]',
                      tokens.patterns.form.input,
                      fieldState.error && 'border-destructive'
                    )}
                  />
                  {fieldState.error?.message && (
                    <span className="text-destructive text-sm">
                      {fieldState.error.message}
                    </span>
                  )}
                </div>
              )}
            />
          )}
        </>
      )}

      {contactsFeatureEnabled &&
        (!showNotes ? (
          <Button
            size="xs"
            density="compact"
            width="full"
            align="start"
            type="button"
            variant="ghost"
            onClick={() => setShowNotes(true)}
          >
            + Add General Notes
          </Button>
        ) : (
          <Controller
            control={control as Control<Partial<Contact>>}
            name="notes"
            render={({ field, fieldState }) => (
              <div className={tokens.patterns.form.spacing}>
                <label
                  htmlFor="contactNotes"
                  className={tokens.patterns.form.label}
                >
                  General Notes
                </label>
                <Textarea
                  id="contactNotes"
                  placeholder="Add general notes about this contact..."
                  value={(field.value as string) || ''}
                  onChange={(e) => field.onChange(e.target.value)}
                  rows={4}
                  className={cn(
                    'min-h-[80px]',
                    tokens.patterns.form.input,
                    fieldState.error && 'border-destructive'
                  )}
                />
                {fieldState.error?.message && (
                  <span className="text-destructive text-sm">
                    {fieldState.error.message}
                  </span>
                )}
              </div>
            )}
          />
        ))}
    </Fragment>
  );
}
